// DEPENDENCIES
import { FormikValues } from "formik";

// TYPES
import {
  Field,
  MidgardQuestions,
  PersonType,
  Step,
  StepFourPF,
} from "../../types";

// UTILS
import { getHeritageDocuments, maxFileSize, validDniTypes } from "../../utils";
import {
  handleSendStep,
  isDev,
  replaceSpecialCharacters,
} from "../../app/utils";
import {
  deleteIndividualDocument,
  sendIndividualDocument,
} from "../../connectors/connectors";
import { logAnalyticsAndHotjarEvent } from "../../main";
import {
  filesNotRequired,
  radioOrSingleSelectRequired,
} from "../../validations";
import {
  getQualifiedInvestorField,
  getQualifiedInvestorType,
} from "../shared/qualifiedInvestorField";

const investmentProfileStep = (
  questions: MidgardQuestions,
  onSendStep?: (
    formValues: FormikValues | null,
    step: Record<string, StepFourPF>,
  ) => Promise<void>,
): Omit<Step, "step"> => {
  const formattedQuestions = questionsToFields(questions);

  const indexOfHeritageOrigin = formattedQuestions.findIndex(
    (field) => field.name === `investmentProfile.${questionsUUIDs.heritage}`,
  );

  const heritageOptions = questions
    .find((q) => q.externalCode === questionsUUIDs.heritage)
    ?.choices.map((choice) => choice.option);

  const monthlyIncomeOptions = questions
    .find((q) => q.externalCode === questionsUUIDs.monthlyIncome)
    ?.choices.map((choice) => choice.option);

  const heritageFieldInput: Field = {
    conditional: [
      {
        name: `investmentProfile.${questionsUUIDs.heritage}`,
        value: heritageOptions?.[heritageOptions.length - 1] || "", // Last option is the highest value
      },
      {
        name: `investmentProfile.${questionsUUIDs.monthlyIncome}`,
        value: monthlyIncomeOptions?.[monthlyIncomeOptions.length - 1] || "", // Last option is the highest value
      },
      {
        name: "pep",
        value: ["FAMILY", "DIRECT"],
      },
    ],
    name: "heritageFiles",
    type: "file",
    canUploadMultipleFiles: true,
    label: "Documentación patrimonial",
    validation: filesNotRequired(validDniTypes, maxFileSize),
    initialValue: "",
    infoText: `Cargá un archivo JPG, PNG, HEIC, HEIF o PDF de hasta ${maxFileSize}mb.`,
    infoTextPosition: "top",
    maxSize: maxFileSize,
    validTypes: validDniTypes,
  };

  // Add new fields dynamically after the "investmentProfile.b2ea30a0-9574-4196-bbce-9812a98020bd" question
  formattedQuestions.splice(indexOfHeritageOrigin + 1, 0, heritageFieldInput);

  return {
    label: "investmentProfile",
    name: "Perfil inversor",
    subSteps: [
      {
        step: 1,
        subtitle: "El riesgo a tomar según el tipo de inversiones.",
        titleOfStep: "Perfil inversor",
        fields: [
          ...formattedQuestions.filter((_, index) => index < 5),
          ...getQualifiedInvestorField(),
        ] as Field[],
      },
      {
        step: 2,
        subtitle: "Declaración jurada de ingresos.",
        titleOfStep: "Información patrimonial",
        fields: formattedQuestions.filter((_, index) => index >= 5) as Field[],
        onContinue: async (values, dispatch, state) => {
          try {
            if (!state || !dispatch)
              throw new Error("Error: state or dispatch not found");

            const {
              onboardingData: { draftUUID, formValues, sendedSteps },
            } = state;
            const step = getParsedStep(questions, values);

            if (onSendStep) {
              await onSendStep(formValues, { stepFour: step });
            } else {
              await handleSendStep({
                dispatch,
                personType: "PHYSICAL" as PersonType,
                email: values.email,
                sendedSteps,
                stepId: 4,
                step,
              });
            }
            logAnalyticsAndHotjarEvent("step_investor_profile_completed_pf");

            await handleHeritageDocuments(draftUUID, values);
          } catch (error) {
            console.log(error, "Error at step six");
            throw new Error("Error al enviar los datos en el paso 6");
          }
        },
      },
    ],
  };
};

const TWO_COLUMN_QUESTIONS = [
  "Objetivo de inversión",
  "Horizonte de inversión previsto",
];

const questionsToFields = (questions: MidgardQuestions) =>
  questions.map((question) => {
    const options = question.choices.map((choice) => {
      return { value: choice.option, label: choice.option };
    });

    const isRiskProfileQuestion =
      question.externalCode === questionsUUIDs.riskProfile;

    return {
      name: `investmentProfile.${question.externalCode}`,
      type: "select",
      label: isRiskProfileQuestion ? "Riesgo a asumir" : question.text,
      placeholder: "Seleccioná una opción",
      validation: radioOrSingleSelectRequired(options),
      options,
      tooltip: isRiskProfileQuestion
        ? {
            label: "Ver tabla de riesgo",
            modal: "modal-risk-profile",
          }
        : undefined,
      selectType: "single",
      initialValue: "",
      columns: TWO_COLUMN_QUESTIONS.includes(question.text) ? 2 : 1,
    };
  }) as Field[];

const getParsedStep = (questions: MidgardQuestions, values: FormikValues) => {
  const { investmentProfile } = values;

  const choices = Object.keys(investmentProfile);

  const choicesParsed = choices
    .map(
      (choice) =>
        findExternalCodeOfValue(questions, choice, values)?.externalCode,
    )
    .filter((code): code is string => code !== undefined);

  return {
    "@type": "4_PHYSICAL",
    investmentProfile: {
      choices: [...choicesParsed],
    },
    qualifiedInvestorType: getQualifiedInvestorType(values),
  };
};

// Find the external code of the value (the answer of question)
// Each question has a externalCode and each answer too, so we need to find the externalCode of the answer about the question
const findExternalCodeOfValue = (
  questions: MidgardQuestions,
  externalCode: string,
  values: FormikValues,
) => {
  const question = questions.find(
    (question) => question.externalCode === externalCode,
  );
  return question?.choices.find(
    (choice) => choice.option === values.investmentProfile[externalCode],
  );
};

const handleHeritageDocuments = async (
  draftUUID: string,
  values: FormikValues,
) => {
  const { cuit, heritageFiles } = values;

  if (heritageFiles) {
    const heritageDocuments = await getHeritageDocuments(draftUUID, cuit);

    // Send to back
    for (const file of heritageFiles) {
      if (file instanceof File) {
        const alreadySent = heritageDocuments?.some(
          (doc) => doc.originalFileName === replaceSpecialCharacters(file.name),
        );
        if (alreadySent) continue;
        await sendIndividualDocument({
          draftUUID,
          documentType: "WORTH_STATEMENT",
          file: file,
          cuit,
        });
      }
    }

    // Delete from back
    const docsToDelete = heritageDocuments?.filter(
      (doc) =>
        !heritageFiles.some(
          (file: File) =>
            replaceSpecialCharacters(file.name) === doc.originalFileName,
        ),
    );

    if (docsToDelete) {
      for (const docToDelete of docsToDelete)
        await deleteIndividualDocument({
          draftUUID,
          documentType: "WORTH_STATEMENT",
          cuit,
          documentUUID: docToDelete.uuid,
        });
    }
  }
};

const devQuestionsUUIDs = {
  heritage: "3bf7f3bf-5717-4cfe-9002-2890ee8e2838",
  monthlyIncome: "e92d83e2-c1da-4cec-bb4b-4c785bc0d78d",
  riskProfile: "1f794542-ccb7-4128-892b-26f3a0c676e4",
};

const prodQuestionsUUIDs = {
  heritage: "dc93dce4-b113-4b2f-b2a9-2584a46ff6b6",
  monthlyIncome: "8494bf9f-475a-4296-8dfe-6ae19b33d04b",
  riskProfile: "30d210d5-189c-4033-a694-dcf65a254bfd",
};

const questionsUUIDs = isDev ? devQuestionsUUIDs : prodQuestionsUUIDs;

export default investmentProfileStep;
